import { useState } from "react";
import { useEffect, useRef } from "react";

import { usePositioner } from "@remirror/react";

import { truncate } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { Image, Loader, Menu } from "simplydo/react/ui";

import type { RemirrorMention } from "./useMention";

const Wrapper = styled.div<{ $isActive: boolean; $left: number; $top: number }>`
  position: absolute;
  z-index: 999;
  min-width: 300px;
  background: #fff;
  display: ${({ $isActive }) => ($isActive ? "block" : "none")};
  border: 1px solid ${({ theme }) => theme?.primaryColour || "#000"};
  border-radius: 4px;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  overflow: hidden;
  box-shadow:
    0 4px 8px rgba(12, 43, 65, 0.25),
    0 0 1px rgba(12, 43, 65, 0.31);
`;

const Avatar = styled(Image)`
  object-fit: cover;
  width: 28px;
  height: 28px;

  &&& {
    margin-right: 10px;
  }
`;

const Emoji = styled.div`
  font-size: 28px;
  align-self: center;
  margin-right: 10px;
`;

const Label = styled.div`
  font-size: 15px;
  font-weight: bold;
`;

const Description = styled.div`
  color: #777;
`;

type DropdownProps = {
  suggestions: Array<
    | RemirrorMention
    | {
        id?: string;
        emoji?: string;
        label?: string;
        description?: string;
        image?: string;
      }
  >;
  loading: boolean;
  isActive: boolean;
  activeIndex: number;
  createMention: (index: number) => void;
};

const Dropdown = ({
  suggestions,
  loading,
  isActive,
  activeIndex,
  createMention,
}: DropdownProps) => {
  const posState = usePositioner("nearestWord", isActive);
  const { x: left, y: bottom } = posState;

  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!suggestions.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + suggestions.length) % suggestions.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          createMention(highlightedIndex);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, suggestions.length, highlightedIndex, createMention]);

  return (
    <Wrapper
      // Remirror's positioner ref must be attached during render.
      // eslint-disable-next-line react-hooks/refs -- ref is provided by usePositioner for DOM positioning
      ref={posState.ref}
      $top={(bottom ?? 0) + 20}
      $left={left}
      $isActive={isActive}
      style={{ minHeight: 60 }}
    >
      {loading ?
        <Loader size="small" centered="inline" active={loading} />
      : null}
      {isActive ?
        <Menu
          vertical
          style={{ width: "100%", minHeight: 60, marginTop: 0 }}
          ref={menuRef}
        >
          {suggestions.length > 0 || loading ?
            suggestions.map((mention, index) => {
              const isHighlighted = index === highlightedIndex;
              return (
                <Menu.Item
                  key={mention.id ?? mention.emoji ?? mention.label}
                  style={isHighlighted ? { background: "#f0f4fa" } : {}}
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    createMention(index);
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {mention.image ?
                      <Avatar avatar src={mention.image} />
                    : null}
                    {mention.emoji ?
                      <Emoji>{mention.emoji}</Emoji>
                    : null}
                    <div>
                      <Label>{mention.label || ""}</Label>
                      <Description>
                        {truncate(mention.description || "", 50)}
                      </Description>
                    </div>
                  </div>
                </Menu.Item>
              );
            })
          : <Menu.Item>
              <Label>Sorry, no results found</Label>
              <Description>Start typing or adjust your query.</Description>
            </Menu.Item>
          }
        </Menu>
      : null}
    </Wrapper>
  );
};

export default Dropdown;
